import { Button } from "components/DesignSystem/Button/Button";
import { useWindowSize } from "hooks/useWindowSize";
import { Banner } from "components/DesignSystem/Banner/Banner";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useToast } from "hooks/useToast";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { CLOSING_DOCUMENT_SUMMARY_CUSTOM_TYPE } from "constants/chatType";
import { Period } from "../FinancialClosing";
import {
  ChecklistItem,
  useAddSummaryToDocumentChannelMutation,
  useGetFinancialClosingDocumentSummaryQuery,
  useGetFinancialClosingQuery,
  useGetRnDBifurcationQuery,
  useUpdateFinancialClosingStatusMutation,
} from "store/apis/financialClosing";
import {
  CHECKLIST_ITEM_SECTION,
  CLOSING_STATUS,
  DOCUMENT_STATUS,
  FINALIZE_FINANCIALS_LABEL,
  finalizeFinancialsOrder,
  RND_ONBOARDING_STEP_INDEX,
} from "constants/financialClosing";
import { BackendError } from "types/utils/error";
import { FinalizeFinancialConfirmationModal } from "../FinalizeFinancialConfirmationModal";
import { RequestChangesConfirmationModal } from "../RequestChangesConfirmationModal";
import { ChangeRequestsAccordion } from "../ChangeRequestsAccordion";
import { ChangeRequestActions } from "../ChangeRequestActions";
import { SuccessAndRdHandoffModal } from "../SuccessAndRdHandoffModal";
import { RnDReviewModal } from "../RnDReviewModal";
import { RnDOnboarding, useRnDOnboardingState } from "../RnDOnboarding";
import { FinancialReviewPreview } from "./FinancialReviewPreview";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Tab from "components/DesignSystem/Tab/Tab";
import { FinancialReviewAIChat } from "./FinancialReviewAIChat";
import { SingleArrowIcon } from "components/icons/SingleArrowIcon";
import CheckCircleFilled from "static/images/CheckCircleFilled.svg";
import classNames from "classnames";

type FinancialReviewModalProps = {
  isOpen: boolean;
  close: () => void;
  financialClosingId: string;
  entityId: string;
};

export type FinancialReviewFile =
  | (Pick<
      ChecklistItem,
      | "uuid"
      | "type"
      | "file_data"
      | "resolved_by_admin"
      | "file_url"
      | "summary_sent_to_chat"
    > & {
      isClosingDocument: true;
    })
  | (Pick<ChecklistItem, "uuid" | "file_data" | "file_url"> & {
      isClosingDocument: false;
    });

export const MarkReviewedAndContinueButton = ({
  currentFile,
  entityId,
  financialClosingId,
  channelId,
  parentMessageId,
  isUpdatingCheckList,
  onCheckListChange,
  getNextSequentialIndex,
  currentFileNum,
  totalFilesNum,
  confirmModalFinancial,
  allReviewed,
  files,
}: {
  currentFile: FinancialReviewFile;
  entityId: string;
  financialClosingId: string;
  channelId: string;
  parentMessageId?: string;
  isUpdatingCheckList: boolean;
  onCheckListChange: (args: {
    checklistItemId: string;
    resolved_by_admin: boolean;
  }) => Promise<void>;
  getNextSequentialIndex: (currentIdx: number) => void;
  currentFileNum: number;
  totalFilesNum: number;
  confirmModalFinancial: ReturnType<typeof useModal>;
  allReviewed: boolean;
  files: FinancialReviewFile[];
}) => {
  const { alertToast } = useToast();
  const [addSummaryToDocument] = useAddSummaryToDocumentChannelMutation();

  const { data: summaryData } = useGetFinancialClosingDocumentSummaryQuery(
    {
      entityId,
      financialClosingId,
      documentId: currentFile.file_data?.uuid || "",
    },
    {
      skip:
        !currentFile ||
        !currentFile.isClosingDocument ||
        !financialClosingId ||
        !entityId ||
        !currentFile.file_data,
      refetchOnMountOrArgChange: true,
    }
  );
  const summary = summaryData?.summary || "";

  const handleAddSummary = async () => {
    if (
      !currentFile ||
      !currentFile.isClosingDocument ||
      currentFile.summary_sent_to_chat ||
      !currentFile.file_data?.uuid ||
      !channelId ||
      !parentMessageId
    )
      return;

    try {
      await addSummaryToDocument({
        entityId,
        channelUrl: channelId,
        text: summary,
        parentMessageId: parentMessageId,
        customType: CLOSING_DOCUMENT_SUMMARY_CUSTOM_TYPE,
        metadata: {
          summary_document_id: currentFile.file_data.uuid,
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };
  const handleReview = async (finalise?: boolean) => {
    await onCheckListChange({
      checklistItemId: currentFile?.uuid,
      resolved_by_admin: true,
    });
    if (
      currentFile.isClosingDocument &&
      !currentFile.summary_sent_to_chat &&
      summary
    ) {
      await handleAddSummary();
    }
    if (finalise) {
      confirmModalFinancial.open();
    }
    getNextSequentialIndex(currentFileNum);
  };

  const isLastFile = currentFileNum === totalFilesNum - 1;
  const allReviewedExcludingLast = files
    .filter((f) => f.isClosingDocument)
    .filter((f, idx) => idx !== totalFilesNum - 1)
    .every((f) => f.resolved_by_admin);

  if (allReviewedExcludingLast && isLastFile) {
    return (
      <Button
        customType="primary"
        onClick={() => handleReview(true)}
        isLoading={isUpdatingCheckList}
      >
        Review & finalise
      </Button>
    );
  }

  return (
    <Button
      customType="primary"
      onClick={() => handleReview(false)}
      isLoading={isUpdatingCheckList}
    >
      Review & continue
    </Button>
  );
};

export const FinancialReviewModal = ({
  isOpen,
  close,
  financialClosingId,
  entityId,
}: FinancialReviewModalProps) => {
  const [currentFileNum, setCurrentFileNum] = useState(0);
  const [activeTab, setActiveTab] = useState<string>("");
  const [showChannel, setShowChannel] = useState(false);
  const { successToast, alertToast } = useToast();
  const confirmModalFinancial = useModal();
  const requestChangesModal = useModal();
  const partialExitModal = useModal();
  const allReviewedExitModal = useModal();
  const successAndRdHandoffModal = useModal();
  const rndReviewModal = useModal();
  const [isRndOnboardingTriggered, setIsRndOnboardingTriggered] =
    useState(false);
  const { isCustomer } = useRoleBasedView();
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateFinancialClosingStatusMutation();

  const { uuid: groupId, entities } = useCurrentChatGroupContext();
  const { isMobile } = useWindowSize();
  const exitButtonSize = isMobile ? "xsmall" : "regular";
  const footerButtonSize = isMobile ? "small" : "regular";
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const channelId = entities.find(
    ({ uuid }) => uuid === entityId
  )?.bookkeeping_channel;

  const {
    data: financialClosingDetails,
    isLoading: isLoadingFinancialClosingDetails,
    isSuccess: isSuccessFinancialClosingDetails,
  } = useGetFinancialClosingQuery(
    {
      entityId: entityId,
      financialClosingId: financialClosingId,
    },
    {
      skip: !financialClosingId || !entityId || !isOpen,
    }
  );

  const { data: rndData, isSuccess: isRndDataLoaded } =
    useGetRnDBifurcationQuery(
      { entityId, financialClosingId },
      { skip: !entityId || !financialClosingId || !isOpen }
    );

  const closingReviewed =
    financialClosingDetails?.closing_card_details?.status ===
    CLOSING_STATUS.COMPLETED;

  const hasRndVendors =
    (rndData?.intercompany_compensation?.vendors?.length ?? 0) > 0 ||
    (rndData?.consultant_contractors?.vendors?.length ?? 0) > 0;

  const isRndOnboardingIncomplete =
    closingReviewed &&
    isRndDataLoaded &&
    (rndData?.rnd_onboarding_step ?? 0) < RND_ONBOARDING_STEP_INDEX.COMPLETED;

  const rndOnboarding = useRnDOnboardingState(
    entityId,
    financialClosingId,
    isRndOnboardingTriggered
  );

  const handleReviewRndExpenses = () => {
    if (isRndOnboardingIncomplete) {
      setIsRndOnboardingTriggered(true);
      return;
    }
    rndReviewModal.open();
  };

  const handleRndOnboardingDismiss = () => {
    setIsRndOnboardingTriggered(false);
    rndReviewModal.open();
  };

  const handleViewRndGuide = () => {
    rndOnboarding.revisit();
    setIsRndOnboardingTriggered(true);
  };

  const handleRndReviewModalClose = () => {
    rndReviewModal.close();
    close();
  };

  const changeStatus = async (
    payload: {
      status: (typeof CLOSING_STATUS)[keyof typeof CLOSING_STATUS];
      send_completed_financials_email?: boolean;
      rejection_reason?: string;
    },
    successMessage: string,
    modalToClose: ReturnType<typeof useModal>
  ) => {
    try {
      await updateStatus({
        entityId: entityId,
        financialClosingId: financialClosingId,
        payload,
      }).unwrap();

      modalToClose.close();

      if (payload.status === CLOSING_STATUS.COMPLETED && hasRndVendors) {
        successAndRdHandoffModal.open();
        return;
      }

      successToast({ message: successMessage });
      close();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const onChangeStatus = (sendEmail: boolean) =>
    changeStatus(
      {
        status: CLOSING_STATUS.COMPLETED,
        send_completed_financials_email: sendEmail,
      },
      "Financials finalized and sent to your email successfully",
      confirmModalFinancial
    );

  const onRequestChanges = (reason: string) =>
    changeStatus(
      { status: CLOSING_STATUS.REJECTED, rejection_reason: reason },
      "Your request for changes has been sent",
      requestChangesModal
    );

  const {
    checklist_items = [],
    closing_card_details,
    other_financials,
  } = financialClosingDetails || {};

  const period = closing_card_details?.period || "";

  const finalizeFinancials = checklist_items.filter(
    ({ section, document_type }) =>
      section === CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS &&
      document_type !== DOCUMENT_STATUS.NONE
  );

  const finalizeFinancialsFiles: FinancialReviewFile[] = finalizeFinancials
    .map((f) => ({
      uuid: f.uuid,
      type: f.type,
      file_data: f.file_data,
      resolved_by_admin: f.resolved_by_admin,
      file_url: f.file_url,
      isClosingDocument: true,
      summary_sent_to_chat: f.summary_sent_to_chat,
    }))
    .sort(
      (a, b) =>
        finalizeFinancialsOrder.indexOf(
          a.type as (typeof finalizeFinancialsOrder)[number]
        ) -
        finalizeFinancialsOrder.indexOf(
          b.type as (typeof finalizeFinancialsOrder)[number]
        )
    );

  const otherFiles: FinancialReviewFile[] = (other_financials || []).map(
    (file) => ({
      uuid: file.uuid,
      file_data: file,
      file_url: file.url ?? "",
      isClosingDocument: false,
    })
  );
  const files: FinancialReviewFile[] = [
    ...finalizeFinancialsFiles,
    ...otherFiles,
  ];

  const currentFile = files[currentFileNum];

  const getNextSequentialIndex = (currentIdx: number) => {
    if (files.length === 0 || currentIdx >= files.length - 1) return -1;
    const nextIdx = currentIdx + 1;
    if (nextIdx !== -1) {
      setCurrentFileNum(nextIdx);
      setActiveTab(files[nextIdx]?.uuid ?? "");
    }
  };

  const getPrevSequentialIndex = (currentIdx: number) => {
    if (files.length === 0 || currentIdx <= 0) return -1;
    const prevIdx = currentIdx - 1;
    if (prevIdx !== -1) {
      setCurrentFileNum(prevIdx);
      setActiveTab(files[prevIdx]?.uuid ?? "");
    }
  };

  const onTabChange = (index: number) => {
    setCurrentFileNum(index);
    setActiveTab(files[index]?.uuid ?? "");
  };

  const allReviewed =
    files.length > 0 &&
    files.filter((f) => f.isClosingDocument).every((f) => f.resolved_by_admin);

  const closingRejected =
    closing_card_details?.status === CLOSING_STATUS.REQUEST_CHANGES;

  const closingAmended =
    closing_card_details?.status === CLOSING_STATUS.AMENDED;

  const isViewOnly = closingReviewed || closingRejected || closingAmended;

  const showChangeRequestActions = closingRejected && !isCustomer;

  const handleAmended = () => {
    close();
    history.push(link("/books/financial-closing"));
  };

  const changeRequests = closing_card_details?.change_requests ?? [];
  const hasChangeRequests = changeRequests.length > 0;

  const isFinaliseDisabled = closingRejected || !allReviewed;

  let finaliseTooltipMessage: string | false = false;
  if (closingRejected) {
    finaliseTooltipMessage = "Changes were requested, hence cant be finalised";
  }
  if (!closingRejected && !allReviewed) {
    finaliseTooltipMessage = "View all the documents to finalise";
  }

  const showRndPendingBanner =
    closingReviewed && hasRndVendors && !rndData?.rnd_review_completed;

  const showRndClassifiedBanner =
    closingReviewed && hasRndVendors && !!rndData?.rnd_review_completed;

  const hasTopSection =
    showRndPendingBanner || showRndClassifiedBanner || hasChangeRequests;

  const handleModalClose = () => {
    if (!isCustomer || isViewOnly || !isSuccessFinancialClosingDetails) {
      close();
      return;
    }
    if (!allReviewed) {
      partialExitModal.open();
      return;
    }
    allReviewedExitModal.open();
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={handleModalClose}>
        <Modal.Content size="xxxl" className="t-rounded-md">
          <Modal.Header className="!t-px-4">
            <div className="t-px-2 t-pt-1 sm:t-px-0 sm:t-min-w-0">
              <Modal.Title>
                <div className="t-flex t-gap-2 t-items-center sm:t-flex-col sm:t-items-start sm:t-gap-0">
                  {closingReviewed || closingAmended ? (
                    <div>Financial closing:</div>
                  ) : (
                    <div>Review Financials for</div>
                  )}
                  {closing_card_details ? (
                    <div>
                      <Period
                        end_date={closing_card_details?.end_date}
                        start_date={closing_card_details?.start_date}
                      />
                    </div>
                  ) : (
                    <div className="t-w-32 t-h-6">
                      <Skeleton className=" t-w-full t-h-full" />
                    </div>
                  )}
                </div>
              </Modal.Title>
              <Modal.Subtitle>
                <div className="t-flex t-flex-row t-gap-3 t-text-body-sm t-text-text-30 sm:t-flex-col sm:t-gap-0">
                  {closing_card_details?.creator?.name && (
                    <div>{`Created by ${closing_card_details.creator.name}`}</div>
                  )}
                  {closingReviewed && (
                    <div className="t-flex t-flex-row t-gap-3">
                      <div className="t-w-[1px] t-bg-neutral-20 t-h-3 t-rounded-full t-self-center sm:t-hidden" />

                      {closing_card_details?.reviewer?.name && (
                        <div>{`Reviewed by ${closing_card_details.reviewer.name}`}</div>
                      )}
                    </div>
                  )}
                </div>
              </Modal.Subtitle>
            </div>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body asChild>
            <div className="t-m-0 t-w-full t-h-[650px] t-flex t-flex-row t-overflow-hidden">
              <div
                className={classNames("t-overflow-auto t-h-full", {
                  "t-flex-1": showChannel,
                  "t-w-full": !showChannel,
                })}
              >
                <Async.Root
                  isSuccess={isSuccessFinancialClosingDetails}
                  isLoading={isLoadingFinancialClosingDetails}
                  isEmpty={files.length === 0}
                  customLoader={
                    <div className="t-h-full t-w-full t-grid t-grid-rows-6 t-gap-2">
                      <Skeleton className="t-row-span-6 t-w-full t-h-full" />
                    </div>
                  }
                >
                  <Async.Success>
                    {currentFile ? (
                      <Tab.Root
                        defaultValue={files[0].uuid}
                        value={activeTab || currentFile.uuid}
                      >
                        <div className="t-flex t-flex-col t-gap-4 t-h-full t-w-full">
                          {hasChangeRequests && (
                            <div className="t-px-8 t-pt-8 sm:t-px-4 sm:t-pt-4">
                              <ChangeRequestsAccordion
                                changeRequests={changeRequests}
                              />
                            </div>
                          )}
                          {showRndPendingBanner && (
                            <div className="t-px-8 t-pt-8 sm:t-px-4 sm:t-pt-4">
                              <Banner
                                type="warning"
                                title="Review Research and Development expenses"
                                description="Confirm R&D bifurcation across vendors to help us calculate your taxes"
                                cta={
                                  <Button
                                    type="button"
                                    customType="secondary"
                                    size="small"
                                    onClick={handleReviewRndExpenses}
                                  >
                                    Review Research and Development expenses
                                  </Button>
                                }
                              />
                            </div>
                          )}
                          {showRndClassifiedBanner && (
                            <div className="t-px-8 t-pt-8 sm:t-px-4 sm:t-pt-4">
                              <Banner
                                type="success"
                                title="R&D expenses classified"
                                description="You've reviewed and classified your R&D expenses across vendors."
                                cta={
                                  <Button
                                    type="button"
                                    customType="link"
                                    size="small"
                                    onClick={rndReviewModal.open}
                                  >
                                    View details
                                  </Button>
                                }
                              />
                            </div>
                          )}
                          <div
                            className={classNames(
                              "t-border-0 t-border-b t-border-solid t-border-neutral-10 t-px-8 sm:t-px-4 t-box-border t-sticky t-top-0 t-bg-white t-z-filter-head",
                              {
                                "t-pt-8": !hasTopSection,
                              }
                            )}
                          >
                            <Tab.List className="sm:!t-gap-4">
                              {files.map((item, index) => (
                                <Tab.Trigger
                                  value={item.uuid}
                                  key={item.uuid}
                                  onClick={() => onTabChange(index)}
                                >
                                  <p className="t-text-body-sm t-m-0 t-text-nowrap t-text-center t-min-w-36 sm:t-min-w-0 t-pb-3">
                                    {item.isClosingDocument ? (
                                      <div className="t-flex t-items-center t-gap-2 t-justify-center">
                                        {FINALIZE_FINANCIALS_LABEL[item.type]}
                                        {item.resolved_by_admin && (
                                          <img
                                            className="t-w-4 t-h-4"
                                            src={CheckCircleFilled}
                                            alt="Reviewed"
                                          />
                                        )}
                                      </div>
                                    ) : (
                                      item.file_data?.document_name
                                    )}
                                  </p>
                                </Tab.Trigger>
                              ))}
                            </Tab.List>
                          </div>
                          <Tab.Content value={currentFile.uuid}>
                            <div className="t-px-8 sm:t-px-4 t-flex t-h-full t-w-full t-justify-center">
                              <FinancialReviewPreview
                                key={currentFile?.uuid}
                                currentFile={currentFile}
                                groupId={groupId}
                                channelId={channelId!}
                                entityId={entityId}
                                parentMessageId={
                                  closing_card_details?.review_message_id!
                                }
                                financialClosingId={financialClosingId}
                                isViewOnly={isViewOnly}
                                setShowChannel={setShowChannel}
                              />
                            </div>
                          </Tab.Content>
                        </div>
                      </Tab.Root>
                    ) : null}
                  </Async.Success>
                </Async.Root>
              </div>
              {showChannel && channelId && (
                <div className="t-h-full t-w-[390px] t-border-0 t-border-l t-border-solid t-border-neutral-10">
                  <FinancialReviewAIChat
                    channelId={channelId}
                    parentMessageId={closing_card_details?.review_message_id!}
                    showChannel={showChannel}
                    setShowChannel={setShowChannel}
                  />
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="t-flex t-w-full t-justify-between t-gap-3 sm:t-flex-col sm:t-gap-2">
              <div className="sm:t-w-full">
                {!isViewOnly && isCustomer && (
                  <Button
                    customType="danger_outlined"
                    size={footerButtonSize}
                    block={isMobile}
                    onClick={requestChangesModal.open}
                  >
                    Request for changes
                  </Button>
                )}
              </div>
              <div className="t-flex t-gap-3 sm:t-w-full sm:t-gap-2">
                {!showChangeRequestActions &&
                  (currentFileNum > 0 || isViewOnly) && (
                    <Button
                      customType="secondary"
                      size={footerButtonSize}
                      onClick={() => getPrevSequentialIndex(currentFileNum)}
                      disabled={currentFileNum === 0}
                    >
                      <SingleArrowIcon />
                      <span>Prev</span>
                    </Button>
                  )}
                {!showChangeRequestActions &&
                  (currentFileNum < files.length - 1 || isViewOnly) && (
                    <Button
                      customType="secondary"
                      size={footerButtonSize}
                      onClick={() => getNextSequentialIndex(currentFileNum)}
                      disabled={currentFileNum === files.length - 1}
                    >
                      <span>Next</span>
                      <span className="t-rotate-180">
                        <SingleArrowIcon />
                      </span>
                    </Button>
                  )}
                {showChangeRequestActions && (
                  <ChangeRequestActions
                    entityId={entityId}
                    financialClosingId={financialClosingId}
                    onAmended={handleAmended}
                  />
                )}
                {!closingReviewed &&
                  !closingAmended &&
                  !showChangeRequestActions && (
                    <ConditionalToolTip condition={finaliseTooltipMessage}>
                      <span className="sm:t-flex-1">
                        <Button
                          customType="primary"
                          size={footerButtonSize}
                          block={isMobile}
                          onClick={confirmModalFinancial.open}
                          disabled={isFinaliseDisabled}
                        >
                          Finalise financials
                        </Button>
                      </span>
                    </ConditionalToolTip>
                  )}
              </div>

              <RequestChangesConfirmationModal
                show={requestChangesModal.isOpen}
                onClose={() => requestChangesModal.close()}
                onConfirm={onRequestChanges}
                period={period}
                isLoading={isUpdatingStatus}
              />

              <FinalizeFinancialConfirmationModal
                show={confirmModalFinancial.isOpen}
                onClose={() => confirmModalFinancial.close()}
                onConfirm={onChangeStatus}
                period={period}
                isLoading={isUpdatingStatus}
                showEmailCheckbox={isCustomer ? false : true}
              />

              <SuccessAndRdHandoffModal
                isOpen={successAndRdHandoffModal.isOpen}
                filingPeriod={closing_card_details?.period || ""}
                onReviewNow={() => {
                  successAndRdHandoffModal.close();
                  handleReviewRndExpenses();
                }}
                onDismiss={() => {
                  successAndRdHandoffModal.close();
                  close();
                }}
              />

              <RnDOnboarding
                onboarding={rndOnboarding}
                onDismiss={handleRndOnboardingDismiss}
              />

              <RnDReviewModal
                isOpen={rndReviewModal.isOpen}
                onClose={handleRndReviewModalClose}
                onViewGuide={handleViewRndGuide}
                entityId={entityId}
                financialClosingId={financialClosingId}
                isFormEditable
                closingDate={closing_card_details?.end_date}
              />
            </div>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>

      <Modal.Root
        open={partialExitModal.isOpen}
        onOpenChange={partialExitModal.close}
      >
        <Modal.Content size="small">
          <StatusModal
            type="warning"
            title="Exit without completing your review?"
            showCloseButton={false}
          >
            <div className="t-flex t-flex-col t-gap-2.5">
              <span className="t-text-body-sm t-text-text-60">
                You haven't finished reviewing all the financial documents for
                this period.
              </span>
              <span className="t-italic t-text-body-sm t-text-text-30">
                If you exit now, the period will remain open, and you won't
                receive the finalized files in your inbox until the review is
                completed.
              </span>
            </div>
          </StatusModal>
          <Modal.FooterButtonGroup>
            <Button
              customType="secondary"
              size={exitButtonSize}
              className="sm:!t-px-1"
              onClick={() => {
                partialExitModal.close();
                close();
              }}
            >
              <span className="sm:t-text-caption">Exit anyway</span>
            </Button>
            <Button
              customType="primary"
              size={exitButtonSize}
              className="sm:!t-px-1"
              onClick={partialExitModal.close}
            >
              <span className="sm:t-text-caption">Continue reviewing</span>
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>

      <Modal.Root
        open={allReviewedExitModal.isOpen}
        onOpenChange={allReviewedExitModal.close}
      >
        <Modal.Content size="small">
          <StatusModal
            type="warning"
            title="Are you sure you want to exit?"
            showCloseButton={false}
          >
            <div className="t-flex t-flex-col t-gap-2.5">
              <span className="t-text-body-sm t-text-text-60">
                You've viewed all the financial documents, but haven't marked
                the financials as finalized yet.
              </span>
              <span className="t-italic t-text-body-sm t-text-text-30">
                If you exit now, the period will remain open and you won't
                receive the finalized financials in your inbox.
              </span>
            </div>
          </StatusModal>
          <Modal.FooterButtonGroup>
            <Button
              customType="secondary"
              size={exitButtonSize}
              className="sm:!t-px-1"
              onClick={() => {
                allReviewedExitModal.close();
                close();
              }}
            >
              <span className="sm:t-text-caption">Exit without finalizing</span>
            </Button>
            <Button
              customType="primary"
              size={exitButtonSize}
              className="sm:!t-px-1"
              onClick={() => {
                allReviewedExitModal.close();
                confirmModalFinancial.open();
              }}
            >
              <span className="sm:t-text-caption">Review & finalize now</span>
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
